import React from 'react';
import { ClipboardListIcon, LoaderCircleIcon, PackageCheckIcon } from 'lucide-react';
import { NisoloProduct } from '../hooks/useNisoloProducts';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function NisoloProductTable({
  products,
  loading,
  error
}: {
  products: NisoloProduct[];
  loading: boolean;
  error: string | null;
}) {
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="nisolo-products-heading" data-id="element-1610" data-mp="1rsk44eeh7m9u">
      <div className="flex items-center justify-between gap-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1611" data-mp="1wm54ue7geucq">
        <div className="flex items-center gap-2" data-id="element-1612" data-mp="1htbaxnrfqboj">
          <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1613" data-mp="1mvaf2zv01cmj" />
          <h2 id="nisolo-products-heading" className="text-base font-semibold" data-id="element-1614" data-mp="18u7rujs9pwa1">Product inventory</h2>
        </div>
        <span className="text-xs text-ink/50" data-id="element-1615" data-mp="1cv031ikpab8y">{products.length.toLocaleString('en-US')} products</span>
      </div>

      {loading && <div className="flex items-center justify-center gap-2 px-5 py-14 text-sm text-ink/55" data-id="element-1616" data-mp="2jvtzs2fo2i62">
          <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-1617" data-mp="2ewqt6dk1g3vz" />
          Loading product inventory…
        </div>}

      {error && !loading && <p className="px-5 py-14 text-center text-sm text-ink/55 sm:px-6" data-id="element-1618" data-mp="2otdfz7vxd50n">{error}</p>}

      {!loading && !error && <div className="max-h-[520px] overflow-auto" data-id="element-1619" data-mp="210wrofllggsw">
          <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1620" data-mp="1w5hhpyqpj51r">
            <thead className="sticky top-0 bg-cream/95 text-xs text-ink/50 backdrop-blur" data-id="element-1621" data-mp="2atkq1es82stq">
              <tr data-id="element-1622" data-mp="262m6m6bxtem9">
                <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1623" data-mp="2622lkgqzqa40">Product</th>
                <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1624" data-mp="1ic8aa3mbktlv">Type</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1625" data-mp="1ddonk0zwjdsg">Variants</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1626" data-mp="1dbucdeefn01k">Units</th>
                <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1627" data-mp="22zq2oqjcodap">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10" data-id="element-1628" data-mp="2btmbl3ni7t86">
              {products.map(product => <tr key={product.handle} data-id="element-1629" data-mp="2gtqroski2lxv">
                  <th scope="row" className="px-5 py-4 text-left font-medium text-ink sm:px-6" data-id="element-1630" data-mp="2gspeahg0hncz">
                    <span className="flex items-center gap-3" data-id="element-1631" data-mp="1k4j6xmrpggv0">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ink/10 bg-cream" data-id="element-1632" data-mp="1o3rziob0e8oz">
                        {product.image ? <img src={product.image} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-1633" data-mp="1y396d91zysmj" /> : <PackageCheckIcon className="h-4 w-4 text-ink/25" aria-hidden="true" data-id="element-1634" data-mp="34ijth5nszo0e" />}
                      </span>
                      <span className="min-w-0" data-id="element-1635" data-mp="xv2m3xrlzzer">
                        <span className="block max-w-xs truncate" data-id="element-1636" data-mp="xtrj405zmvop">{product.title}</span>
                        <span className="mt-0.5 block text-xs font-normal text-ink/45" data-id="element-1637" data-mp="svye3agf87q0">
                          {product.status.toLocaleLowerCase() === 'active' ? 'Active' : product.status || 'Draft'}
                          {product.sizes.length > 0 && ` · ${product.sizes.length} sizes`}
                        </span>
                      </span>
                    </span>
                  </th>
                  <td className="px-3 py-4 text-ink/65" data-id="element-1638" data-mp="a3drubv890v6">{product.type || '—'}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-ink/65" data-id="element-1639" data-mp="56x4vkzrksjo">{product.variants}</td>
                  <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1640" data-mp="82kiem5evxg">{product.units.toLocaleString('en-US')}</td>
                  <td className="px-5 py-4 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-1641" data-mp="1zhsix2tpwztu">{product.price > 0 ? currency(product.price) : '—'}</td>
                </tr>)}
              {products.length === 0 && <tr data-id="element-1642" data-mp="27kazlyuxqvk0"><td colSpan={5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1643" data-mp="27kb4ac5vrjqc">No products found in the file.</td></tr>}
            </tbody>
          </table>
        </div>}
    </article>;
}